import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  collection,
  doc,
  getDocs,
  updateDoc,
} from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { useAuth } from './AuthContext';
import { Customer } from '../types/typesCustomer';
import { Repair } from '../types/typesRepair';
import { Device } from '../types/typesDevices';
import { Employee } from '../types/typesEmployees';
import { Store } from '../types/typesStores';
import { repairListCategoryFromStatus } from '../utils/repairListCategory';
import {
  deleteCustomerFromHybrid,
  deleteRepairFromHybrid,
  fetchCustomerByIdFromHybrid,
  fetchCustomersPageFromHybrid,
  fetchDevicesFromHybrid,
  fetchRepairByIdFromHybrid,
  fetchRepairsFromHybrid,
  fetchStoresFromHybrid,
  mergeCustomersInHybrid,
  saveCustomerToHybrid,
  saveDeviceToHybrid,
  saveRepairToHybrid,
  saveStoreToHybrid,
} from './entitiesHybridService';
import {
  auditUserFromAuth,
  buildPaymentCompletedAudit,
  buildRepairDeletedAudit,
  logAuditActivity,
} from './activityAuditLog';
import {
  buildCustomerUpdateActivityLog,
  buildRepairUpdateActivityLog,
} from '../utils/activityLogDetails';

const isFirestoreTimestamp = (value: any) =>
  value &&
  typeof value === 'object' &&
  typeof value.toDate === 'function' &&
  typeof value.seconds === 'number' &&
  typeof value.nanoseconds === 'number';

/** עוטף הבטחת Firestore ב-timeout — מונע "תקיעה" של ה-WebChannel שמעכבת את ה-UI לנצח. */
function withFirestoreTimeout<T>(promise: Promise<T>, timeoutMs: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => {
      reject(new Error(`Firestore request timed out after ${timeoutMs}ms`));
    }, timeoutMs);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error) => {
        clearTimeout(timer);
        reject(error);
      },
    );
  });
}

const sanitizeFirestoreData = <T,>(data: T): T => {
  if (data === undefined) {
    return undefined as unknown as T;
  }

  if (data === null || typeof data !== 'object' || data instanceof Date || isFirestoreTimestamp(data)) {
    return data;
  }

  if (Array.isArray(data)) {
    return data
      .map((item) => sanitizeFirestoreData(item))
      .filter((item) => item !== undefined) as unknown as T;
  }

  const sanitized: Record<string, unknown> = {};
  Object.entries(data as Record<string, unknown>).forEach(([key, value]) => {
    const sanitizedValue = sanitizeFirestoreData(value);
    if (sanitizedValue !== undefined) {
      sanitized[key] = sanitizedValue;
    }
  });
  return sanitized as T;
};

async function assertCustomerPhoneUnique(
  currentCustomers: Map<string, Customer>,
  phone: string,
  excludeCustomerId?: string,
): Promise<void> {
  const trimmed = String(phone || '').trim();
  if (!trimmed) return;
  const conflict = Array.from(currentCustomers.values()).find(
    (c) =>
      c.id !== excludeCustomerId &&
      phonesDigitsCompatible(String(c.phone || ''), trimmed),
  );
  if (conflict) {
    throw new Error(`לקוח עם מספר טלפון ${trimmed} כבר קיים במערכת`);
  }
}

function findCustomerConflictByPhone(
  currentCustomers: Map<string, Customer>,
  phone: string,
  excludeCustomerId?: string,
): Customer | undefined {
  const trimmed = String(phone || '').trim();
  if (!trimmed) return undefined;
  return Array.from(currentCustomers.values()).find(
    (c) =>
      c.id !== excludeCustomerId &&
      phonesDigitsCompatible(String(c.phone || ''), trimmed),
  );
}

function normalizePhoneDigits(value: string): string {
  return String(value || '').replace(/\D/g, '');
}

function phonesDigitsCompatible(a: string, b: string): boolean {
  const da = normalizePhoneDigits(a);
  const db = normalizePhoneDigits(b);
  if (!da || !db) return false;
  if (da === db) return true;
  const tail = (s: string) => (s.length >= 9 ? s.slice(-9) : s);
  return tail(da) === tail(db);
}

function findCustomerInMapByPhone(map: Map<string, Customer>, phone: string): Customer | undefined {
  const list = Array.from(map.values());
  for (let i = 0; i < list.length; i += 1) {
    const c = list[i];
    if (phonesDigitsCompatible(String(c.phone || ''), phone)) return c;
  }
  return undefined;
}

/**
 * לפני שמירת קריאה: אם יש שם+טלפון ללקוח ואין קישור תקף לרשומת לקוח — מקשר לפי טלפון או יוצר לקוח חדש.
 * לא רץ על «טלפון חנות». כשל ביצירה לא חוסם שמירת קריאה (רק מתועד).
 */
async function ensureCustomerLinkedForRepairSave(
  repair: Repair,
  customersMap: Map<string, Customer>,
  setCustomers: React.Dispatch<React.SetStateAction<Map<string, Customer>>>,
): Promise<Partial<Repair>> {
  if (repair.customerPhoneIsStore) return {};

  const phoneTrim = String(repair.customerPhone || '').trim();
  const nameTrim = String(repair.customerName || '').trim();
  const digits = normalizePhoneDigits(phoneTrim);
  if (!digits || !nameTrim) return {};

  const existingId = String(repair.customerId || '').trim();
  if (existingId) {
    let known = customersMap.get(existingId);
    if (!known) {
      try {
        const remote = await fetchCustomerByIdFromHybrid(existingId);
        if (remote?.id) {
          known = remote;
          setCustomers((prev) => {
            const next = new Map(prev);
            next.set(remote.id, remote);
            return next;
          });
        }
      } catch {
        // נמשיך לזיהוי לפי טלפון
      }
    }
    if (known) {
      const knownPhone = normalizePhoneDigits(String(known.phone || ''));
      // לקוח מקושר בלי טלפון + יש טלפון בקריאה → חפש את בעל הטלפון האמיתי ומזג
      if (!knownPhone && digits) {
        let byPhone = findCustomerInMapByPhone(customersMap, phoneTrim);
        if (!byPhone) {
          try {
            const res = await fetchCustomersPageFromHybrid({
              limit: 20,
              offset: 0,
              q: phoneTrim,
              includeTotal: false,
              timeoutMs: 4500,
            });
            byPhone = res.data.find((c) => phonesDigitsCompatible(String(c.phone || ''), phoneTrim));
          } catch {
            // ignore
          }
        }
        if (byPhone?.id && byPhone.id !== existingId) {
          try {
            const merged = await mergeCustomersInHybrid(byPhone.id, [existingId]);
            const keep = merged.keep;
            setCustomers((prev) => {
              const next = new Map(prev);
              next.delete(existingId);
              next.set(keep.id, keep);
              return next;
            });
            return {
              customerId: keep.id,
              customerName: nameTrim || String(keep.name || '').trim(),
              customerPhone: phoneTrim,
              customerPhoneNormalized: digits,
            };
          } catch (e) {
            // eslint-disable-next-line no-console
            console.warn('[SyncService] merge empty-phone customer failed', e);
            return {
              customerId: byPhone.id,
              customerName: nameTrim,
              customerPhone: phoneTrim,
              customerPhoneNormalized: digits,
            };
          }
        }
      }
      return {};
    }
  }

  let linked = findCustomerInMapByPhone(customersMap, phoneTrim);
  if (!linked) {
    try {
      const res = await fetchCustomersPageFromHybrid({
        limit: 20,
        offset: 0,
        q: phoneTrim,
        includeTotal: false,
        timeoutMs: 4500,
      });
      linked = res.data.find((c) => phonesDigitsCompatible(String(c.phone || ''), phoneTrim));
    } catch {
      // ignore
    }
  }

  if (linked?.id) {
    setCustomers((prev) => {
      const next = new Map(prev);
      if (!next.has(linked!.id)) next.set(linked!.id, linked!);
      return next;
    });
    return {
      customerId: linked.id,
      customerName: nameTrim,
      customerPhone: phoneTrim,
      customerPhoneNormalized: digits,
    };
  }

  const parts = nameTrim.split(/\s+/).filter(Boolean);
  const firstName = parts[0] || nameTrim;
  const lastName = parts.slice(1).join(' ').trim();
  const now = new Date().toISOString();
  const company = String(repair.customerCompany || '').trim();
  const newRow: Customer = {
    id: '',
    firstName,
    lastName,
    name: nameTrim,
    phone: phoneTrim,
    email: '',
    city: '',
    store: '',
    ...(company ? { company } : {}),
    active: true,
    createdAt: now,
    updatedAt: now,
    lastActionDate: now,
  };

  try {
    await assertCustomerPhoneUnique(customersMap, phoneTrim);
  } catch {
    try {
      const res = await fetchCustomersPageFromHybrid({
        limit: 20,
        offset: 0,
        q: phoneTrim,
        includeTotal: false,
        timeoutMs: 4500,
      });
      const hit = res.data.find((c) => phonesDigitsCompatible(String(c.phone || ''), phoneTrim));
      if (hit?.id) {
        setCustomers((prev) => {
          const next = new Map(prev);
          if (!next.has(hit.id)) next.set(hit.id, hit);
          return next;
        });
        return {
          customerId: hit.id,
          customerName: nameTrim,
          customerPhone: phoneTrim,
          customerPhoneNormalized: digits,
        };
      }
    } catch {
      // fall through
    }
    return {};
  }

  try {
    const saved = await saveCustomerToHybrid(newRow);
    setCustomers((prev) => {
      const next = new Map(prev);
      next.set(saved.id, saved);
      return next;
    });
    return {
      customerId: saved.id,
      customerName: nameTrim,
      customerPhone: phoneTrim,
      customerPhoneNormalized: digits,
    };
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[SyncService] יצירת לקוח אוטומטית נכשלה — נשמרת הקריאה בלי קישור חדש', e);
    try {
      const res = await fetchCustomersPageFromHybrid({
        limit: 20,
        offset: 0,
        q: phoneTrim,
        includeTotal: false,
        timeoutMs: 4500,
      });
      const hit = res.data.find((c) => phonesDigitsCompatible(String(c.phone || ''), phoneTrim));
      if (hit?.id) {
        setCustomers((prev) => {
          const next = new Map(prev);
          if (!next.has(hit.id)) next.set(hit.id, hit);
          return next;
        });
        return {
          customerId: hit.id,
          customerName: nameTrim,
          customerPhone: phoneTrim,
          customerPhoneNormalized: digits,
        };
      }
    } catch {
      // noop
    }
    return {};
  }
}

interface SyncServiceContextType {
  customers: Map<string, Customer>;
  repairs: Map<string, Repair>;
  devices: Map<string, Device>;
  employees: Map<string, Employee>;
  stores: Map<string, Store>;
  
  // פונקציות עדכון
  updateCustomer: (customerId: string, data: Partial<Customer>) => Promise<void>;
  updateRepair: (repairId: string, data: Partial<Repair>, auditBefore?: Partial<Repair>) => Promise<void>;
  /** עדכון מקומי בלבד (ללא Firestore) – למשל אחרי ביטול לכידה מהשרת, כדי שהטבלה תציג מיד */
  updateRepairLocal: (repairId: string, data: Partial<Repair>) => void;
  updateDevice: (deviceId: string, data: Partial<Device>) => Promise<void>;
  updateEmployee: (employeeId: string, data: Partial<Employee>) => Promise<void>;
  updateStore: (storeId: string, data: Partial<Store>) => Promise<void>;
  deleteRepair: (repairId: string) => Promise<void>;
  deleteCustomer: (customerId: string) => Promise<void>;
  refreshCustomers: () => Promise<void>;
  addCustomer: (customerId: string, data: Customer) => Promise<string>;
  addRepair: (repair: Repair) => Promise<Repair>;
  /** חנויות + מכשירים + עובדים — פעם אחת לסשן (משותף לכל העמודים) */
  ensureCatalogLoaded: () => Promise<void>;
  /** תיקונים לייבוא/בדיקת כפילויות — לא בכניסה לאפליקציה */
  ensureRepairsSnapshotLoaded: () => Promise<void>;
}

export const SyncServiceContext = createContext<SyncServiceContextType | null>(null);

export const useSyncService = () => {
  const context = useContext(SyncServiceContext);
  if (!context) {
    throw new Error('useSyncService must be used within a SyncServiceProvider');
  }
  return context;
};

export const SyncServiceProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, role } = useAuth();
  const isExternalViewer = role === 'external_viewer';
  const canSyncInternalCatalog = Boolean(user && role && !isExternalViewer);
  /** תיקונים אחרונים בלבד — האזנה לכל הקולקציה יקרה מדי; רשימת קריאות נטענת בנפרד עם pagination */
  const MAX_SYNCED_REPAIRS = 400;
  /** לקוחות ב-SyncService: מטמון חלקי בלבד כדי לא להעמיס 20K+ רשומות לזיכרון/UI */
  const MAX_SYNCED_CUSTOMERS = 200;
  const [customers, setCustomers] = useState<Map<string, Customer>>(new Map());
  const [repairs, setRepairs] = useState<Map<string, Repair>>(new Map());
  const [devices, setDevices] = useState<Map<string, Device>>(new Map());
  const [employees, setEmployees] = useState<Map<string, Employee>>(new Map());
  const [stores, setStores] = useState<Map<string, Store>>(new Map());

  /** refs כדי שהפונקציות ב-useCallback יקראו תמיד את ה-state העדכני בלי לגרום ל-value של ה-context להשתנות בכל שינוי. */
  const customersRef = useRef(customers);
  const repairsRef = useRef(repairs);
  const devicesRef = useRef(devices);
  const storesRef = useRef(stores);
  const catalogLoadPromiseRef = useRef<Promise<void> | null>(null);
  const repairsSnapshotPromiseRef = useRef<Promise<void> | null>(null);
  useEffect(() => { customersRef.current = customers; }, [customers]);
  useEffect(() => { repairsRef.current = repairs; }, [repairs]);
  useEffect(() => { devicesRef.current = devices; }, [devices]);
  useEffect(() => { storesRef.current = stores; }, [stores]);

  const ensureCatalogLoaded = useCallback(async () => {
    if (!canSyncInternalCatalog) return;
    if (!catalogLoadPromiseRef.current) {
      catalogLoadPromiseRef.current = (async () => {
        // חנויות/דגמים (Postgres) — מהיר; לא תלוי יותר בקריאת ה-users של Firestore.
        // בעבר Promise.all עם getDocs(users) גרם לכך שתקיעת ה-WebChannel (עד ~48ש')
        // עיכבה גם את טעינת הקטלוג לסינון.
        const [storesHybrid, devicesHybrid] = await Promise.all([
          fetchStoresFromHybrid(),
          fetchDevicesFromHybrid(),
        ]);
        setStores(new Map(storesHybrid.map((s) => [s.id, s])));
        setDevices(new Map((devicesHybrid as Device[]).map((d) => [d.id, d])));
        // עובדים מ-Firestore (users) — best-effort עם timeout כדי שתקיעת Firestore
        // לא תשאיר את הקטלוג "תלוי" לנצח. אם נכשל — ממשיכים בלי שמות עובדים.
        try {
          const usersSnapshot = await withFirestoreTimeout(
            getDocs(collection(db, 'users')),
            12000,
          );
          const internalEmployees = usersSnapshot.docs
            .map((d) => ({ id: d.id, ...d.data() } as Employee))
            .filter((employee) => employee.role !== 'external_viewer');
          setEmployees(new Map(internalEmployees.map((employee) => [employee.id, employee])));
        } catch (employeesError) {
          console.warn(
            'טעינת עובדים מ-Firestore נכשלה/עברה timeout — ממשיכים בלי שמות עובדים.',
            employeesError,
          );
        }
      })();
    }
    try {
      await catalogLoadPromiseRef.current;
    } catch (e) {
      catalogLoadPromiseRef.current = null;
      throw e;
    }
  }, [canSyncInternalCatalog]);

  const ensureRepairsSnapshotLoaded = useCallback(async () => {
    if (!canSyncInternalCatalog) return;
    if (repairsRef.current.size > 0) return;
    if (!repairsSnapshotPromiseRef.current) {
      repairsSnapshotPromiseRef.current = (async () => {
        const response = await fetchRepairsFromHybrid({ limit: MAX_SYNCED_REPAIRS, offset: 0 });
        setRepairs(new Map(response.data.map((r) => [r.id, r])));
      })();
    }
    try {
      await repairsSnapshotPromiseRef.current;
    } catch (e) {
      repairsSnapshotPromiseRef.current = null;
      throw e;
    }
  }, [canSyncInternalCatalog]);

  const refreshCustomers = useCallback(async () => {
    if (!canSyncInternalCatalog) return;
    const response = await fetchCustomersPageFromHybrid({
      limit: MAX_SYNCED_CUSTOMERS,
      offset: 0,
      // includeTotal=true תואם למפתח שעמוד הלקוחות מבקש — כך ה-dedup מאחד את שתי הבקשות לאחת.
      includeTotal: true,
      timeoutMs: 15000,
    });
    const map = new Map((response.data || []).map((c) => [c.id, c]));
    setCustomers(map);
  }, [canSyncInternalCatalog]);

  // לקוחות: טעינה מושהית — לא חוסמת כניסה לדשבורד; נדרש לעמוד לקוחות / העשרת קריאות.
  useEffect(() => {
    if (!canSyncInternalCatalog) return;
    let cancelled = false;
    const loadCustomers = async () => {
      try {
        const response = await fetchCustomersPageFromHybrid({
          limit: MAX_SYNCED_CUSTOMERS,
          offset: 0,
          // includeTotal=true תואם למפתח שעמוד הלקוחות מבקש — מאחד את הבקשה הכפולה דרך ה-dedup.
          includeTotal: true,
          timeoutMs: 15000,
        });
        const map = new Map((response.data || []).map((c) => [c.id, c]));
        if (!cancelled) setCustomers(map);
      } catch (e) {
        console.error('Error loading customers (paged):', e);
      }
    };
    let idleId: number | undefined;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    if (typeof window.requestIdleCallback === 'function') {
      idleId = window.requestIdleCallback(() => {
        void loadCustomers();
      }, { timeout: 3000 });
    } else {
      timeoutId = setTimeout(() => {
        void loadCustomers();
      }, 800);
    }
    return () => {
      cancelled = true;
      if (idleId != null && typeof window.cancelIdleCallback === 'function') {
        window.cancelIdleCallback(idleId);
      }
      if (timeoutId != null) clearTimeout(timeoutId);
    };
  }, [canSyncInternalCatalog]);

  // קטלוג (חנויות/מכשירים/עובדים) — טעינה משותפת; תיקונים נטענים lazy דרך ensureRepairsSnapshotLoaded
  useEffect(() => {
    if (!canSyncInternalCatalog) {
      if (!user || isExternalViewer) {
        catalogLoadPromiseRef.current = null;
        repairsSnapshotPromiseRef.current = null;
      }
      return;
    }
    void ensureCatalogLoaded().catch((e) => {
      console.error('Error loading catalog in SyncService:', e);
    });
  }, [user, isExternalViewer, canSyncInternalCatalog, ensureCatalogLoaded]);

  // פונקציות עדכון
  const updateCustomer = useCallback(async (customerId: string, data: Partial<Customer>) => {
    if (!customerId) {
      throw new Error('Customer ID is required for update');
    }

    try {
      const currentCustomers = customersRef.current;
      // Update the name field if firstName or lastName is provided
      const updateData = { ...data };
      if (data.firstName !== undefined || data.lastName !== undefined) {
        const currentCustomer = currentCustomers.get(customerId);
        if (currentCustomer) {
          updateData.name = `${data.firstName ?? currentCustomer.firstName} ${data.lastName ?? currentCustomer.lastName}`.trim();
        }
      }

      if (updateData.phone) {
        try {
          await assertCustomerPhoneUnique(currentCustomers, String(updateData.phone), customerId);
        } catch (uniqueError: any) {
          const currentCustomerForMerge = currentCustomers.get(customerId);
          const currentPhoneEmpty = !normalizePhoneDigits(String(currentCustomerForMerge?.phone || ''));
          if (currentPhoneEmpty) {
            const conflict = findCustomerConflictByPhone(
              currentCustomers,
              String(updateData.phone || ''),
              customerId,
            );
            if (conflict?.id) {
              const merged = await mergeCustomersInHybrid(conflict.id, [customerId]);
              const saved = merged.keep;
              setCustomers((prev) => {
                const next = new Map(prev);
                next.delete(customerId);
                next.set(saved.id, saved);
                return next;
              });
              setRepairs((prev) => {
                let anyMutation = false;
                const next = new Map(prev);
                prev.forEach((repair, id) => {
                  if (repair.customerId !== customerId) return;
                  next.set(id, {
                    ...repair,
                    customerId: saved.id,
                    customerPhone: String(saved.phone || repair.customerPhone || ''),
                  });
                  anyMutation = true;
                });
                return anyMutation ? next : prev;
              });
              return;
            }
          }
          throw uniqueError;
        }
      }
      let currentCustomer = currentCustomers.get(customerId);
      // אם הלקוח לא ב־Map המקומי (טעינה חלקית) — נשלים מהשרת כדי לא לדרוס טלפון קיים
      if (!currentCustomer || !normalizePhoneDigits(String(currentCustomer.phone || ''))) {
        try {
          const remote = await fetchCustomerByIdFromHybrid(customerId);
          if (remote?.id) {
            currentCustomer = remote;
            setCustomers((prev) => {
              const next = new Map(prev);
              next.set(remote.id, remote);
              return next;
            });
          }
        } catch {
          // ignore — ניפול לבדיקת הטלפון למטה
        }
      }

      const payload = {
        ...(currentCustomer || { id: customerId }),
        ...updateData,
        id: customerId,
      } as Customer;

      // עדכון חלקי בלי שדה phone לא אמור למחוק טלפון קיים
      if (!normalizePhoneDigits(String(updateData.phone ?? '')) && currentCustomer) {
        payload.phone = currentCustomer.phone;
      }

      if (!normalizePhoneDigits(String(payload.phone || ''))) {
        throw new Error('לא ניתן לשמור לקוח ללא מספר טלפון');
      }

      let saved: Customer;
      try {
        saved = await saveCustomerToHybrid(payload);
      } catch (error: any) {
        const existing = error?.existingCustomer as Customer | undefined;
        const currentPhoneEmpty = !normalizePhoneDigits(String(currentCustomer?.phone || ''));
        // לקוח בלי טלפון שמנסים לשים עליו מספר שכבר קיים → מזגים לבעל הטלפון
        if (Number(error?.statusCode || 0) === 409 && existing?.id && existing.id !== customerId && currentPhoneEmpty) {
          const merged = await mergeCustomersInHybrid(existing.id, [customerId]);
          saved = merged.keep;
          setCustomers((prev) => {
            const next = new Map(prev);
            next.delete(customerId);
            next.set(saved.id, saved);
            return next;
          });
          setRepairs((prev) => {
            let anyMutation = false;
            const next = new Map(prev);
            prev.forEach((repair, id) => {
              if (repair.customerId !== customerId) return;
              next.set(id, { ...repair, customerId: saved.id });
              anyMutation = true;
            });
            return anyMutation ? next : prev;
          });
          return;
        }
        throw error;
      }

      setCustomers((prev) => {
        const next = new Map(prev);
        next.set(customerId, saved);
        return next;
      });

      const auditUser = auditUserFromAuth(user);
      const customerLabel =
        String(saved.name || '').trim() ||
        `${saved.firstName || ''} ${saved.lastName || ''}`.trim() ||
        saved.phone ||
        customerId;
      const customerLog = buildCustomerUpdateActivityLog(customerLabel, currentCustomer, saved);
      if (customerLog && auditUser) {
        logAuditActivity(auditUser, customerLog);
      }

      // עדכון שם הלקוח המדה-נורמלי בקריאות שירות בזיכרון — כך שטבלת הקריאות תתרענן מיידית.
      const freshName =
        (saved.name && String(saved.name).trim()) ||
        `${saved.firstName || ''} ${saved.lastName || ''}`.trim();
      const freshPhone = String(saved.phone || '').trim();
      const freshPhoneDigits = freshPhone.replace(/\D/g, '');
      const prevPhoneDigits = String(currentCustomer?.phone || '').replace(/\D/g, '');
      setRepairs((prev) => {
        let anyMutation = false;
        const next = new Map(prev);
        prev.forEach((repair, id) => {
          const matchesById = Boolean(repair.customerId) && repair.customerId === customerId;
          const repairPhoneDigits = String(repair.customerPhone || '').replace(/\D/g, '');
          const matchesByPhone =
            !matchesById &&
            repairPhoneDigits.length > 0 &&
            (repairPhoneDigits === prevPhoneDigits || repairPhoneDigits === freshPhoneDigits);
          if (!matchesById && !matchesByPhone) return;
          let rowMutated = false;
          const updatedRepair: Repair = { ...repair };
          if (freshName && updatedRepair.customerName !== freshName) {
            updatedRepair.customerName = freshName;
            rowMutated = true;
          }
          if (!updatedRepair.customerId && customerId) {
            updatedRepair.customerId = customerId;
            rowMutated = true;
          }
          if (freshPhone && updatedRepair.customerPhone !== freshPhone) {
            updatedRepair.customerPhone = freshPhone;
            updatedRepair.customerPhoneNormalized = freshPhoneDigits || updatedRepair.customerPhoneNormalized;
            rowMutated = true;
          }
          if (rowMutated) {
            next.set(id, updatedRepair);
            anyMutation = true;
          }
        });
        return anyMutation ? next : prev;
      });
    } catch (error) {
      console.error('Error updating customer and related repairs:', error);
      throw error;
    }
  }, [user]);

  const updateRepair = useCallback(async (
    repairId: string,
    data: Partial<Repair>,
    auditBefore?: Partial<Repair>,
  ) => {
    try {
      const currentRepairs = repairsRef.current;
      const currentRepair = currentRepairs.get(repairId) || null;
      const updateData: any = {
        ...data,
        updatedAt: new Date().toISOString()
      };

      /**
       * תיקון שלא ב־Map (למשל לא ב־רשימה המסונכרנת המקומית) — עדיין אפשר PATCH לשרת אם יש הקשר:
       * מספר קריאה, או עדכון סטטוס עם מזהה תיקון תקף (עדכון מרובה מהמסך / סקריפטים).
       */
      if (!currentRepair) {
        const cn = String(updateData.callNumber ?? '').trim();
        const rid = String(repairId || '').trim();
        const hasStatusPatch = updateData.status !== undefined && updateData.status !== null;
        const hasInvoicePatch = updateData.priorityInvoiceIssued !== undefined;
        if (!cn && !(rid && hasStatusPatch) && !(rid && hasInvoicePatch)) {
          throw new Error('No document to update');
        }
      }

      // בדיקת ייחודיות מקומית רק כשיש שינוי אמיתי במספר הקריאה.
      // כשהתיקון לא נמצא ב-map המקומי (טעינה חלקית/מטמון ישן), לא נחסום עדכון תקין.
      const nextCallNumber = String(updateData.callNumber ?? '').trim();
      const currentCallNumber = String(currentRepair?.callNumber ?? '').trim();
      const callNumberChanged = Boolean(nextCallNumber) && nextCallNumber !== currentCallNumber;
      if (callNumberChanged) {
        const existingRepair = Array.from(currentRepairs.values()).find(
          (r) => String(r.callNumber || '').trim() === nextCallNumber && r.id !== repairId,
        );
        if (existingRepair) {
          throw new Error(`קריאת שירות עם מספר קריאה ${nextCallNumber} כבר קיימת במערכת`);
        }
      }

      const mergedStatus =
        updateData.status !== undefined ? updateData.status : (currentRepair?.status ?? '');
      updateData.repairListCategory = repairListCategoryFromStatus(mergedStatus);
      let base = (currentRepair || null) as Repair | null;
      if (!base?.id) {
        try {
          base = await fetchRepairByIdFromHybrid(repairId);
        } catch {
          base = null;
        }
      }
      const payload = { ...(base || {}), ...sanitizeFirestoreData(updateData), id: repairId } as Repair;

      let customerLinkPatch: Partial<Repair> = {};
      try {
        customerLinkPatch = await ensureCustomerLinkedForRepairSave(
          payload,
          customersRef.current,
          setCustomers,
        );
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('[SyncService] ensureCustomerLinkedForRepairSave', e);
      }
      const finalPayload = { ...payload, ...customerLinkPatch } as Repair;
      const saved = await saveRepairToHybrid(finalPayload);

      // Update local state immediately
      setRepairs((prev) => {
        const next = new Map(prev);
        next.set(repairId, saved);
        return next;
      });

      const auditUser = auditUserFromAuth(user);
      const beforeRepair = (auditBefore ?? currentRepair ?? base) as Partial<Repair> | null;
      const updateLog = buildRepairUpdateActivityLog(
        String(finalPayload.callNumber || beforeRepair?.callNumber || saved.callNumber || ''),
        beforeRepair,
        finalPayload,
      );
      if (updateLog && auditUser) {
        const repairStoreName = String(finalPayload.storeName || beforeRepair?.storeName || '').trim();
        logAuditActivity(auditUser, {
          ...updateLog,
          repairId,
          repairCallNumber: String(saved.callNumber || currentRepair?.callNumber || '').trim() || undefined,
          storeName: repairStoreName || undefined,
          metadata: {
            ...(updateLog.metadata || {}),
            storeName: repairStoreName || undefined,
            source: 'sync_service',
          },
        });
      }

      const beforePaymentStatus = String(beforeRepair?.paymentStatus || '').trim();
      const afterPaymentStatus = String(finalPayload.paymentStatus || '').trim();
      if (
        auditUser &&
        afterPaymentStatus === 'completed' &&
        beforePaymentStatus !== 'completed'
      ) {
        const paidAmount = Number(
          finalPayload.paymentGatewayPaidAmount ?? finalPayload.paymentAmount ?? 0,
        );
        logAuditActivity(auditUser, {
          ...buildPaymentCompletedAudit({
            callNumber: String(finalPayload.callNumber || saved.callNumber || '').trim() || 'ללא מספר',
            repairId,
            amount: paidAmount,
            paymentMethod: finalPayload.paymentMethod,
            gateway: finalPayload.paymentGateway,
            source: 'repair_save',
          }),
        });
      }
    } catch (error) {
      const msg = String((error as any)?.message || error || '');
      // מקרה צפוי (למשל בייבוא) – ניתן לטפל חיצונית עם fallback ל-addRepair בלי "רעש" בקונסול
      if (!msg.includes('No document to update')) {
        console.error('Error updating repair:', error);
      }
      throw error;
    }
  }, [user]);

  const updateRepairLocal = useCallback((repairId: string, data: Partial<Repair>) => {
    setRepairs((prev) => {
      const next = new Map(prev);
      const cur = next.get(repairId);
      next.set(repairId, { ...(cur || {}), ...data, id: repairId } as Repair);
      return next;
    });
  }, []);

  const updateDevice = useCallback(async (deviceId: string, data: Partial<Device>) => {
    const current = devicesRef.current.get(deviceId);
    const payload = { ...(current || { id: deviceId }), ...data, id: deviceId } as Device;
    const saved = await saveDeviceToHybrid(payload);
    setDevices((prev) => {
      const next = new Map(prev);
      next.set(deviceId, saved);
      return next;
    });
  }, []);

  const updateEmployee = useCallback(async (employeeId: string, data: Partial<Employee>) => {
    await updateDoc(doc(db, 'employees', employeeId), sanitizeFirestoreData(data));
    const relatedRepairs = Array.from(repairsRef.current.values())
      .filter(repair => repair.employeeId === employeeId);
    for (const repair of relatedRepairs) {
      await updateDoc(doc(db, 'repairs', repair.id), {
        employeeName: data.name,
      });
    }
  }, []);

  const updateStore = useCallback(async (storeId: string, data: Partial<Store>) => {
    const current = storesRef.current.get(storeId);
    const payload = { ...(current || { id: storeId }), ...data, id: storeId } as Store;
    const saved = await saveStoreToHybrid(payload);
    setStores((prev) => {
      const next = new Map(prev);
      next.set(storeId, saved);
      return next;
    });
  }, []);

  const deleteRepair = useCallback(async (repairId: string) => {
    const existing = repairsRef.current.get(repairId);
    await deleteRepairFromHybrid(repairId);
    setRepairs((prev) => {
      const next = new Map(prev);
      next.delete(repairId);
      return next;
    });
    const auditUser = auditUserFromAuth(user);
    if (auditUser) {
      logAuditActivity(auditUser, buildRepairDeletedAudit(existing || { id: repairId }));
    }
  }, [user]);

  const deleteCustomer = useCallback(async (customerId: string) => {
    if (!customerId) {
      throw new Error('Customer ID is required for deletion');
    }

    const existing = customersRef.current.get(customerId);
    try {
      await deleteCustomerFromHybrid(customerId);
      setCustomers((prev) => {
        const next = new Map(prev);
        next.delete(customerId);
        return next;
      });
      const auditUser = auditUserFromAuth(user);
      if (auditUser) {
        const label =
          String(existing?.name || '').trim() ||
          `${existing?.firstName || ''} ${existing?.lastName || ''}`.trim() ||
          existing?.phone ||
          customerId;
        logAuditActivity(auditUser, {
          activityType: 'customer_deleted',
          description: `נמחק לקוח: ${label}`,
          metadata: {
            deletedCustomer: label,
            deletedPhone: existing?.phone || undefined,
            deletedEmail: existing?.email || undefined,
            customerId,
          },
        });
      }
    } catch (error) {
      console.error('Error deleting customer:', error);
      throw error;
    }
  }, [user]);

  const addCustomer = useCallback(async (_customerId: string, data: Customer): Promise<string> => {
    const { id, ...customerData } = data;
    customerData.name = `${data.firstName} ${data.lastName}`;
    if (!customerData.createdAt) customerData.createdAt = new Date().toISOString();
    if (!customerData.updatedAt) customerData.updatedAt = new Date().toISOString();
    if (!customerData.lastActionDate) customerData.lastActionDate = new Date().toISOString();

    const phoneDigits = normalizePhoneDigits(String(customerData.phone || ''));
    if (!phoneDigits || phoneDigits.length < 9) {
      throw new Error('לא ניתן ליצור לקוח ללא מספר טלפון תקין');
    }

    await assertCustomerPhoneUnique(customersRef.current, String(customerData.phone));

    try {
      const saved = await saveCustomerToHybrid({ ...(customerData as Customer), id: '' });
      setCustomers((prev) => {
        const next = new Map(prev);
        next.set(saved.id, saved);
        return next;
      });
      const auditUser = auditUserFromAuth(user);
      if (auditUser) {
        const label =
          String(saved.name || '').trim() ||
          `${saved.firstName || ''} ${saved.lastName || ''}`.trim() ||
          saved.phone;
        logAuditActivity(auditUser, {
          activityType: 'customer_created',
          description: `נוצר לקוח: ${label}`,
          metadata: {
            customerName: label,
            phone: saved.phone || undefined,
            email: saved.email || undefined,
            city: saved.city || undefined,
          },
        });
      }
      return saved.id;
    } catch (error: any) {
      const existing = error?.existingCustomer as Customer | undefined;
      if (existing?.id) {
        setCustomers((prev) => {
          const next = new Map(prev);
          next.set(existing.id, existing);
          return next;
        });
        return existing.id;
      }
      console.error('Error adding customer:', error);
      throw error;
    }
  }, [user]);

  const addRepair = useCallback(async (repair: Repair): Promise<Repair> => {
    const { id, ...repairData } = repair;
    (repairData as any).repairListCategory = repairListCategoryFromStatus(repairData.status);
    if (!repairData.createdAt) repairData.createdAt = new Date().toISOString();
    if (!repairData.updatedAt) repairData.updatedAt = new Date().toISOString();

    // מספר קריאה מוקצה באופן סמכותי בשרת בזמן יצירה, ולכן לא חוסמים יצירה לפי מטמון מקומי חלקי.

    try {
      let customerLinkPatch: Partial<Repair> = {};
      try {
        customerLinkPatch = await ensureCustomerLinkedForRepairSave(
          repair,
          customersRef.current,
          setCustomers,
        );
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('[SyncService] ensureCustomerLinkedForRepairSave (new repair)', e);
      }
      const merged = { ...(repairData as Repair), ...customerLinkPatch };
      const saved = await saveRepairToHybrid({ ...(merged as Repair), id: '' });
      setRepairs((prev) => {
        const next = new Map(prev);
        next.set(saved.id, saved);
        return next;
      });
      const auditUser = auditUserFromAuth(user);
      if (auditUser) {
        logAuditActivity(auditUser, {
          activityType: 'repair_created',
          description: `נוצרה קריאת שירות ${String(saved.callNumber || '').trim() || 'ללא מספר'}`,
          repairId: saved.id,
          repairCallNumber: String(saved.callNumber || '').trim() || undefined,
          storeName: String(saved.storeName || '').trim() || undefined,
          metadata: {
            initialStatus: String(saved.status || '').trim() || undefined,
            customerName: String(saved.customerName || '').trim() || undefined,
            deviceModel: String(saved.deviceModel || '').trim() || undefined,
            storeName: String(saved.storeName || '').trim() || undefined,
            source: 'sync_service',
          },
        });
      }
      return saved;
    } catch (error: any) {
      console.error('Error adding repair:', error);
      throw error;
    }
  }, [user]);

  /** memoize של ה-value ב-context — אחרת הוא נבנה מחדש בכל רינדור וכל הצרכנים מתרנדרים מחדש לשווא. */
  const value = useMemo<SyncServiceContextType>(() => ({
    customers,
    repairs,
    devices,
    employees,
    stores,
    updateCustomer,
    updateRepair,
    updateRepairLocal,
    updateDevice,
    updateEmployee,
    updateStore,
    deleteRepair,
    deleteCustomer,
    refreshCustomers,
    addCustomer,
    addRepair,
    ensureCatalogLoaded,
    ensureRepairsSnapshotLoaded,
  }), [
    customers,
    repairs,
    devices,
    employees,
    stores,
    updateCustomer,
    updateRepair,
    updateRepairLocal,
    updateDevice,
    updateEmployee,
    updateStore,
    deleteRepair,
    deleteCustomer,
    refreshCustomers,
    addCustomer,
    addRepair,
    ensureCatalogLoaded,
    ensureRepairsSnapshotLoaded,
  ]);

  return (
    <SyncServiceContext.Provider value={value}>
      {children}
    </SyncServiceContext.Provider>
  );
}; 